import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';

// ── COLLAB-NOTIF Slice 2 — resource notes (collaborative, permanent) ──────────
// Server-authoritative: the API owns permission (add-set gate → 403), tenant RLS,
// and author resolution. The client only lists + adds. Add is on-confirmation
// (NO optimistic insert) — the list invalidates on success; on error the caller
// keeps the draft text (card 5).

export type ResourceNoteType = 'risk_acceptance' | 'org_risk_acceptance' | 'assessment';

// Roles allowed to ADD a note (executive_viewer excluded — read-only, see-not-add).
export const NOTE_ADD_ROLES = ['super_admin', 'risk_manager', 'analyst'];

export interface ResourceNote {
  id: string;
  resource_type: ResourceNoteType;
  resource_id: string;
  author_user_id: string;
  author_name: string | null;
  author_email: string | null;
  body: string;
  created_at: string;
}

// List notes for one resource (thread order, oldest first). Not fetched until a
// resource id is present (a notification with no linked resource shows no section).
export function useResourceNotes(
  resourceType: ResourceNoteType | null | undefined,
  resourceId: string | null | undefined,
) {
  const { tenantId } = useAuthStore();
  const enabled = !!tenantId && !!resourceType && !!resourceId;
  return useQuery({
    queryKey: ['resource-notes', tenantId, resourceType, resourceId],
    queryFn: async () => {
      const { data } = await apiClient.get('/resource-notes', {
        params: { resource_type: resourceType, resource_id: resourceId },
      });
      return data.data as ResourceNote[];
    },
    enabled,
    staleTime: 30_000,
  });
}

// Add a note. On success the list refetches (the note joins on confirmation).
export function useAddResourceNote(resourceType: ResourceNoteType, resourceId: string) {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async (body: string) => {
      const { data } = await apiClient.post('/resource-notes', {
        resource_type: resourceType,
        resource_id: resourceId,
        body,
      });
      return data.data as ResourceNote;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['resource-notes', tenantId, resourceType, resourceId] });
    },
  });
}
